import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import ReceiptEuro from 'assets/svg/icons/ReceiptEuro';
import AccountableLogoWordV2 from 'components/atoms/SVG/AccountableLogoWordV2';
import IntegrationBadge from './IntegrationBadge';
import NewBankIcon from 'assets/svg/icons/newBankIcon';
import FilePercentage from 'assets/svg/icons/FilePercentage';
import CoinsGroup from 'assets/svg/icons/CoinsGroup';

interface BadgeConfig {
  variant: 'revenues' | 'expenses' | 'taxes';
  labelKey: string;
  position: string;
  delay: number;
}

const BADGE_CONFIGS: BadgeConfig[] = [
  {
    variant: 'taxes',
    labelKey: 'taxes',
    position: 'absolute top-16 left-12 w-fit',
    delay: 0.1,
  },
  {
    variant: 'expenses',
    labelKey: 'expenses',
    position: 'absolute top-24 right-12 w-fit',
    delay: 0.25,
  },
  {
    variant: 'revenues',
    labelKey: 'revenues',
    position: 'absolute bottom-16 left-1/2 -translate-x-1/2 w-fit',
    delay: 0.4,
  },
];

const ICON_MAP = {
  revenues: CoinsGroup,
  expenses: ReceiptEuro,
  taxes: FilePercentage,
};

const HowIsIntegratedVisual: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="relative w-full max-w-[320px] mx-auto h-[300px]">
      {/* Circular dotted background */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="w-80 h-80 rounded-full border border-dashed border-bank-300"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Circular dotted background */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="w-60 h-60 rounded-full border border-dashed border-bank-300"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Inner dotted circle */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="w-44 h-44 rounded-full border border-dashed border-bank-300"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        />
      </div>

      {/* Center badge - Accountable */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-[0px_1px_2px_0px_rgba(27,27,27,0.1),0px_0px_0px_1px_#f6f6f6]"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <NewBankIcon className="w-6 h-6 text-bank-600" />
          <AccountableLogoWordV2 size={80} className="text-bank-600" />
        </motion.div>
      </div>

      {/* Floating integration badges */}
      {BADGE_CONFIGS.map((config) => (
        <div key={config.variant} className={config.position}>
          <IntegrationBadge
            variant={config.variant}
            label={t(config.labelKey)}
            Icon={ICON_MAP[config.variant]}
            delay={config.delay}
          />
        </div>
      ))}
    </div>
  );
};

export default HowIsIntegratedVisual;
